import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { BookOpen, FileText, Loader2, Plus } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { AgentResourcesService } from "@/services/agent/AgentResourcesService";
import type { ResourceDocument } from "@/types/agent/info-links";

interface KbResourceSuggestionsProps {
  agentId: string;
  /** Keys of the knowledge-base files already offered as resources. */
  offeredKeys: ReadonlySet<string>;
  /** Lower-cased file names already in the Resources list, however they got there. */
  offeredFileNames: ReadonlySet<string>;
  /** How many more documents the Resources list can take. */
  slots: number;
  onImported: (documents: ResourceDocument[]) => void;
}

/**
 * Suggests the genie's knowledge-base files as downloads for visitors. The
 * files' existing AI summaries become the resource descriptions, so a
 * well-read knowledge base turns into a described resource list in one click.
 */
export const KbResourceSuggestions = ({
  agentId,
  offeredKeys,
  offeredFileNames,
  slots,
  onImported,
}: KbResourceSuggestionsProps) => {
  const [importingIds, setImportingIds] = useState<ReadonlySet<string>>(new Set());

  const { data: candidates = [], isLoading } = useQuery({
    queryKey: ["kb-resource-candidates", agentId],
    queryFn: () => AgentResourcesService.listKbCandidates(agentId),
    staleTime: 60 * 1000,
  });

  const unoffered = useMemo(
    () =>
      candidates.filter(
        (candidate) =>
          !offeredKeys.has(candidate.key) && !offeredFileNames.has(candidate.fileName.toLowerCase()),
      ),
    [candidates, offeredKeys, offeredFileNames],
  );

  const available = useMemo(
    () => unoffered.filter((candidate) => !importingIds.has(candidate.key)),
    [unoffered, importingIds],
  );

  if (isLoading || (available.length === 0 && importingIds.size === 0)) return null;

  const handleImport = async (keys: string[]) => {
    const ids = keys.slice(0, slots);
    if (ids.length === 0) {
      toast.error("The Resources list is full — remove a document to add another.");
      return;
    }
    setImportingIds((prev) => new Set([...prev, ...ids]));
    try {
      const { documents, failed } = await AgentResourcesService.importFromKb(agentId, ids);
      if (documents.length > 0) onImported(documents);
      if (failed.length > 0) {
        toast.error(
          failed.length === 1
            ? `Couldn't add ${failed[0].name ?? "that file"}: ${failed[0].reason}`
            : `${failed.length} files couldn't be added.`,
        );
      }
    } catch (error) {
      console.error("Importing knowledge base resources failed", error);
      toast.error("Couldn't add those files right now. Please try again.");
    } finally {
      setImportingIds((prev) => {
        const next = new Set(prev);
        ids.forEach((id) => next.delete(id));
        return next;
      });
    }
  };

  return (
    <div className="rounded-lg border border-dashed border-genie-primary/40 bg-genie-primary-light/40 p-3">
      <div className="flex flex-wrap items-start justify-between gap-2 mb-2">
        <div className="min-w-0">
          <div className="flex items-center gap-1.5 text-sm font-medium text-gray-700">
            <BookOpen className="w-4 h-4 text-genie-primary" />
            From your knowledge base
          </div>
          <p className="text-xs text-gray-500 mt-0.5">
            Files your Genie already knows. Offering one lets visitors download it.
          </p>
        </div>
        {available.length > 1 && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => void handleImport(available.map((candidate) => candidate.key))}
            disabled={importingIds.size > 0}
            className="gap-1 bg-white"
          >
            <Plus className="w-3.5 h-3.5" />
            Offer all ({Math.min(available.length, slots)})
          </Button>
        )}
      </div>

      <div className="space-y-1.5">
        {unoffered.map((candidate) => {
          const importing = importingIds.has(candidate.key);
          return (
            <div key={candidate.key} className="flex items-center gap-2 rounded-md bg-white p-2 border border-gray-200">
              <FileText className="w-4 h-4 flex-shrink-0 text-gray-400" />
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-gray-900 truncate">{candidate.name}</div>
                <div className="text-xs text-gray-500 line-clamp-1">
                  {candidate.summary ?? `${candidate.extension.toUpperCase()} · AI will describe it once added`}
                </div>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => void handleImport([candidate.key])}
                disabled={importing}
                className="min-h-[44px] sm:min-h-0 flex-shrink-0 gap-1 text-genie-primary hover:text-genie-primary-hover"
              >
                {importing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
                {importing ? "Adding…" : "Offer"}
              </Button>
            </div>
          );
        })}
      </div>
    </div>
  );
};
